<template>
  <v-container>
    <h2 class="pb-4">Settings</h2>
    <v-divider></v-divider>
    <v-card flat>
      <v-card-text>
        <v-row dense>
          <v-col sm="3">
            <v-select
              dense
              v-model="computedValue.input.settings.type"
              label="Forecast Method"
              :items="forecastTypeItems"
              :rules="[(v) => !!v || 'required']"
              @change="change()"
            />
          </v-col>
          <v-col class="pr-6">
            <InputDateTime
              dense
              v-model="computedValue.input.settings.start_of_forecast_date"
              label="Forecast Start Date"
              :dateRules="forecastStartRules"
              :timeRules="timeRules"
              timezone="UTC"
              @change="$emit('change')"
            />
          </v-col>
          <v-col>
            <InputDateTime
              dense
              v-model="computedValue.input.settings.end_of_forecast_date"
              label="Forecast End Date"
              :timeRules="timeRules"
              :dateRules="forecastEndRules"
              timezone="UTC"
              @change="$emit('change')"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col sm="6">
            <v-select
              dense
              v-model="computedValue.input.settings.profile"
              label="Forecast Granularity"
              :items="forecastGranularityItems"
              :rules="[(v) => !!v || 'required']"
              @change="change()"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col sm="3">
            <v-switch dense v-model="toggleHFV" label="High Forecast Value" @change="change()" />
          </v-col>
          <v-col sm="3">
            <v-switch dense v-model="toggleLFV" label="Low Forecast Value" @change="change()" />
          </v-col>
          <v-col sm="6">
            <v-switch
              dense
              v-model="computedValue.input.settings.forecast_limit_lag"
              label="Forecast Limiter"
              @change="change()"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col sm="3">
            <v-text-field
              v-if="toggleHFV"
              dense
              v-model.number="computedValue.input.settings.hfv"
              label="HFV multiplier"
              type="number"
              :rules="hfvRules"
              @change="change()"
            />
          </v-col>
          <v-col sm="3">
            <v-text-field
              v-if="toggleLFV"
              dense
              v-model.number="computedValue.input.settings.lfv"
              label="LFV multiplier"
              type="number"
              :rules="lfvRules"
              @change="change()"
            />
          </v-col>
          <v-col sm="6" v-if="value.input.settings.forecast_limit_lag">
            <v-row dense>
              <v-col sm="6"
                ><v-text-field
                  dense
                  v-model.number="computedValue.input.settings.max_forecast_limit"
                  label="Max Forecast Limit"
                  type="number"
                  @change="change()"
              /></v-col>

              <v-col sm="6"
                ><v-text-field
                  dense
                  v-model.number="computedValue.input.settings.min_forecast_limit"
                  label="Min Forecast Limit"
                  type="number"
                  @change="change()"
              /></v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <template v-if="computedValue.input.settings.type == 'IHR'">
      <h2 class="pb-4 pt-4">Inputs</h2>
      <v-divider></v-divider>
      <v-card flat>
        <v-card-subtitle> <h3>Energy/Ancillary Price Inputs</h3> </v-card-subtitle>
        <v-card-text>
          <v-card outlined>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="computedValue.input.energy_ancillary_inputs.year_start"
                    label="Start Year"
                    :items="startYearItems"
                    dense
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="computedValue.input.energy_ancillary_inputs.year_end"
                    label="End Year"
                    :items="endYearItems"
                    dense
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model.number="computedValue.input.energy_ancillary_inputs.shaping_year"
                    label="Shaping Year"
                    :items="shapingYearItems"
                    dense
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12">
                  <h4 class="pb-4">Energy Price Inputs</h4>
                  <v-divider class="pt-4"></v-divider>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="
                        computedValue.input.energy_ancillary_inputs.energy_settings
                          .energy_price_source
                      "
                      label="Price Source"
                      :items="priceSourceItems"
                      item-text="text"
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                  <v-col sm="12" v-if="energyMarketSelection">
                    <v-row class="pt-4" dense>
                      <InputPriceMarket
                        v-model="markets"
                        energy-price-source="market"
                        ancillary-price-source="custom"
                        dense
                        :node-list="false"
                        :date-range="true"
                        auto-select-first-market
                        @change="$emit('change')"
                      ></InputPriceMarket>
                    </v-row>
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="
                      computedValue.input.energy_ancillary_inputs.energy_settings
                        .energy_price_source == 'custom'
                    "
                  >
                    <InputYearlyProfile
                      v-model="computedValue.input.energy_ancillary_inputs.energy_settings.profile"
                      label="Custom Profile"
                      :num_years="numYears"
                      spread-values
                      fill
                      object-value
                      items-type="profile"
                      profile-type="ENERGY_PRICE"
                      @change="$emit('change')"
                    ></InputYearlyProfile>
                  </v-col>
                </v-col>
                <v-col>
                  <v-row dense>
                    <v-col sm="10">
                      <h4 class="pt-6">Ancillary Price Inputs</h4>
                    </v-col>
                    <v-col sm="2">
                      <v-switch
                        dense
                        v-model="
                          computedValue.input.energy_ancillary_inputs.ancillary_settings.active
                        "
                        label="Active"
                        @change="change()"
                      />
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="
                        computedValue.input.energy_ancillary_inputs.ancillary_settings
                          .ancillary_price_source
                      "
                      label="Price Source"
                      :items="priceSourceItems"
                      item-text="text"
                      :disabled="
                        !computedValue.input.energy_ancillary_inputs.ancillary_settings.active
                      "
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="computedValue.input.energy_ancillary_inputs.ancillary_settings.active"
                  >
                    <v-row class="pt-4" dense>
                      <InputPriceMarket
                        v-model="ancillaryMarkets"
                        :market-only="ancillaryCustomPriceSource === 'custom'"
                        energy-price-source="custom"
                        ancillary-price-source="market"
                        dense
                        :node-list="false"
                        :date-range="true"
                        auto-select-first-market
                        @change="$emit('change')"
                      ></InputPriceMarket>
                    </v-row>
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="
                      ancillaryProductOptions &&
                      computedValue.input.energy_ancillary_inputs.ancillary_settings.active
                    "
                  >
                    <v-select
                      dense
                      v-model="ancillaryProductSelection"
                      label="Ancillary Types"
                      :items="ancillaryProductOptions"
                      item-text="text"
                      item-value="value"
                      multiple
                      chips
                      deletable-chips
                      :rules="[(v) => (v && v.length > 0) || 'select at least one']"
                      @change="change()"
                    />
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="
                      ancillaryCustomPriceSource == 'custom' &&
                      computedValue.input.energy_ancillary_inputs.ancillary_settings.active
                    "
                  >
                    <v-row dense v-for="label in ancillaryProductSelection" :key="label">
                      <v-col sm="12">
                        <InputYearlyProfile
                          :value="ancillaryCustomProfile(label)"
                          @input="setAncillaryCustomProfile(label, $event)"
                          :label="`Custom Profile - ${ancillaryProductLabelText(label)}`"
                          :num_years="numYears"
                          spread-values
                          fill
                          object-value
                          items-type="profile"
                          @change="$emit('change')"
                        ></InputYearlyProfile>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
      <v-card flat>
        <v-card-subtitle> <h3>Fuel Price Inputs</h3> </v-card-subtitle>
        <v-card-text>
          <v-card outlined>
            <v-card-text>
              <v-row dense>
                <v-col sm="12">
                  <h4 class="pb-4">Historical</h4>
                  <v-divider></v-divider>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="
                        computedValue.input.fuel_price_inputs.historical_fuel_price
                          .fuel_price_source
                      "
                      label="Fuel Price Source"
                      :items="fuelSourceItems"
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="
                      computedValue.input.fuel_price_inputs.historical_fuel_price
                        .fuel_price_source === 'market'
                    "
                  >
                    <InputMarketNode
                      market="EIA"
                      label="Historical Source ID"
                      :params="{ query: { Granularity: 'Monthly' } }"
                      v-model="fuelPriceHistoricalSourceElement"
                    ></InputMarketNode>
                  </v-col>

                  <v-col
                    sm="12"
                    v-if="
                      computedValue.input.fuel_price_inputs.historical_fuel_price
                        .fuel_price_source === 'custom'
                    "
                  >
                    <InputYearlyProfile
                      v-model="computedValue.input.fuel_price_inputs.historical_fuel_price.profile"
                      label="Custom Profile"
                      :num_years="numYears"
                      spread-values
                      fill
                      object-value
                      items-type="profile"
                      @change="$emit('change')"
                    ></InputYearlyProfile>
                  </v-col>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="
                        computedValue.input.fuel_price_inputs.historical_fuel_price.fuel_type
                      "
                      label="Fuel Type"
                      :items="fuelTypeItems"
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                </v-col>
                <v-col sm="12">
                  <h4 class="pb-4">Forecast</h4>
                  <v-divider></v-divider>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="
                        computedValue.input.fuel_price_inputs.fuel_price_forecast.fuel_price_source
                      "
                      label="Fuel Price Source"
                      :items="fuelSourceItems"
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                  <v-col
                    sm="12"
                    v-if="
                      computedValue.input.fuel_price_inputs.fuel_price_forecast
                        .fuel_price_source === 'market'
                    "
                  >
                    <InputMarketNode
                      market="EIA-SOURCES"
                      label="Forecast Source ID"
                      v-model="fuelPriceForecastSourceElement"
                    ></InputMarketNode>
                  </v-col>

                  <v-col
                    sm="12"
                    v-if="
                      computedValue.input.fuel_price_inputs.fuel_price_forecast
                        .fuel_price_source === 'custom'
                    "
                  >
                    <InputYearlyProfile
                      v-model="computedValue.input.fuel_price_inputs.fuel_price_forecast.values"
                      label="Values"
                      :num_years="forecastNumYears"
                      spread-values
                      fill
                      object-value
                      items-type="constant"
                      suffix="$/MMBtu"
                      :year-start="forecastYearStart"
                      @change="$emit('change')"
                    ></InputYearlyProfile>
                  </v-col>
                  <v-col sm="12">
                    <v-select
                      dense
                      v-model="computedValue.input.fuel_price_inputs.fuel_price_forecast.fuel_type"
                      label="Fuel Type"
                      :items="fuelTypeItems"
                      :rules="[(v) => !!v || 'required']"
                      @change="change()"
                    />
                  </v-col>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-col sm="12" class="pt-4">
            <v-text-field
              dense
              v-model.number="computedValue.input.fuel_price_inputs.fuel_delivery_charge"
              label="Fuel Delivery Charge"
              type="number"
              :rules="[(v) => !!(v !== '') || 'required']"
              @change="change()"
            />
          </v-col>
        </v-card-text>
      </v-card>
      <v-row>
        <v-col sm="4"> </v-col>
        <v-col sm="4"> </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { DateTime } from "luxon";
import {
  LEGACY_ANCILLARY_LABELS,
  LEGACY_ANCILLARY_SELECTION,
  ANCILLARY_PRODUCT_LABELS,
  isLegacyAncillaryLabels,
  migrateLegacyAncillaryLabels,
} from "@/utils/legacyAncillary";

export default {
  name: "IHRForm",
  components: {
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    InputPriceMarket: () => import("@/components/InputPriceMarket.vue"),
    InputDateTime: () => import("@/components/InputDateTime.vue"),
    InputMarketNode: () => import("@/components/InputMarketNode.vue"),
  },
  mixins: [],
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    const {
      market_name: name,
      index,
      node_id: node,
      zone_id: zone,
      ancillary_settings: ancillarySettings,
    } = this.value.input.energy_ancillary_inputs;
    const {
      market_name: ancillaryName = name,
      index: ancillaryIndex = index,
      node_id: ancillaryNode = node,
      zone_id: ancillaryZone = zone,
    } = ancillarySettings;
    return {
      markets: name ? { name, index, node, zone } : undefined,
      // Always a real object (never undefined): InputPriceMarket mutates
      // this in place via v-model, and an undefined prop would make Vue
      // hand the child its own disconnected default object, silently
      // losing every selection instead of writing back into ancillary_settings.
      // Backward compat: forecasts saved before ancillary had its own market
      // fields only ever had the shared top-level selection.
      ancillaryMarkets: {
        name: ancillaryName || null,
        index: ancillaryIndex || null,
        node: ancillaryNode || null,
        zone: ancillaryZone || null,
      },
      debugAncillaryWatcherFireCount: 0,
      legacyAncillaryLabels: LEGACY_ANCILLARY_LABELS,

      // Ancillary price types per market to allow selection
      marketAncillaryProducts: ANCILLARY_PRODUCT_LABELS,

      legacyAncillarySelection: LEGACY_ANCILLARY_SELECTION,
      priceSourceItems: [
        { text: "Market", value: "market" },
        { text: "Custom", value: "custom" },
      ],
      forecastTypeItems: ["IHR", "Hybrid IHR"],
      toggleHFV: false,
      toggleLFV: false,
      forecastGranularityItems: [
        { text: "Hourly", value: "H" },
        { text: "30 min", value: "30min" },
        { text: "15 min", value: "15min" },
        { text: "5 min", value: "5min" },
      ],
      fuelSourceItems: [
        { text: "Default", value: "default" },
        { text: "Custom", value: "custom" },
        { text: "Market", value: "market" },
      ],
      fuelTypeItems: [
        { text: "Natural Gas", value: "NG" },
        { text: "Diesel", value: "Diesel" },
      ],
      forecastStartRules: [
        (v) => !!v || "required",
        (v) =>
          DateTime.fromISO(v) >= DateTime.fromISO("1900-01-01") ||
          "Should be greater than 1900-01-01",
      ],
      timeRules: [(v) => !!v || "required"],
      hfvRules: [(v) => (!!v && this.toggleHFV) || "required", (v) => v > 0 || "Should be > 0"],
      lfvRules: [(v) => (!!v && this.toggleLFV) || "required", (v) => v > 0 || "Should be > 0"],
      startYearItems: Array(6)
        .fill(new Date().getFullYear())
        .map((v, i) => v - i)
        .reverse(),
      fuelPriceForecastSourceObject: null,
      fuelPriceHistoricalSourceObject: null,
    };
  },
  computed: {
    numYears() {
      return (
        this.value.input.energy_ancillary_inputs.year_end -
        this.value.input.energy_ancillary_inputs.year_start +
        1
      );
    },
    endYearItems() {
      return this.startYearItems.filter(
        (v) => v >= this.computedValue.input.energy_ancillary_inputs.year_start
      );
    },
    shapingYearItems() {
      const { year_start, year_end } = this.computedValue.input.energy_ancillary_inputs;
      // eslint-disable-next-line camelcase
      if (year_start && year_end) {
        // eslint-disable-next-line camelcase
        return this.endYearItems.filter((v) => v <= year_end);
      }
      return [];
    },
    ancillaryCustomPriceSource() {
      return this.value.input.energy_ancillary_inputs.ancillary_settings.ancillary_price_source;
    },
    ancillaryActive() {
      return this.value.input.energy_ancillary_inputs.ancillary_settings.active;
    },
    ancillaryMarketName() {
      const { market_name: marketName, ancillary_settings: ancillarySettings } =
        this.value.input.energy_ancillary_inputs;
      return ancillarySettings.market_name || marketName;
    },
    ancillaryProductOptions() {
      return this.marketAncillaryProducts[this.ancillaryMarketName] || null;
    },
    ancillaryProductSelection: {
      get() {
        const labels =
          this.value.input.energy_ancillary_inputs.ancillary_settings.ancillary_price_labels;
        if (isLegacyAncillaryLabels(labels)) {
          return this.legacyAncillarySelection[this.ancillaryMarketName] || [];
        }
        return labels || [];
      },
      set(selected) {
        this.value.input.energy_ancillary_inputs.ancillary_settings.ancillary_price_labels =
          selected;
        this.$emit("input", this.value);
      },
    },
    fuelPriceForecastSource() {
      return this.value.input.fuel_price_inputs.fuel_price_forecast.fuel_price_source;
    },
    fuelPriceHistoricalSource() {
      return this.value.input.fuel_price_inputs.historical_fuel_price.fuel_price_source;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    forecastStartYear() {
      return this.value.input.settings.start_of_forecast_date;
    },
    forecastYearStart() {
      // UTC Time
      return DateTime.fromISO(this.value.input.settings.start_of_forecast_date, { zone: "UTC" })
        .year;
    },
    forecastNumYears() {
      // If end year is not start of year, include
      let offset = 0;
      if (
        DateTime.fromISO(this.value.input.settings.end_of_forecast_date, { zone: "UTC" }).ts >=
        DateTime.fromISO(this.value.input.settings.end_of_forecast_date, { zone: "UTC" }).startOf(
          "year"
        ).ts
      ) {
        offset = 1;
      }
      return (
        DateTime.fromISO(this.value.input.settings.end_of_forecast_date, { zone: "UTC" }).year +
        offset -
        DateTime.fromISO(this.value.input.settings.start_of_forecast_date, { zone: "UTC" }).year
      );
    },
    energyMarketSelection() {
      return (
        this.value.input.energy_ancillary_inputs.energy_settings.energy_price_source === "market"
      );
    },
    forecastEndRules() {
      return [
        (v) => !!v || "required",
        (v) =>
          DateTime.fromISO(v) >= DateTime.fromISO("1900-01-01") ||
          "Should be greater than 1900-01-01",
        (v) =>
          DateTime.fromISO(v) >=
            DateTime.fromISO(this.value.input.settings.start_of_forecast_date) ||
          `Should be grater than ${this.value.input.settings.start_of_forecast_date.split("T")[0]}`,
      ];
    },
    fuelPriceForecastSourceElement: {
      get() {
        return this.fuelPriceForecastSourceObject;
      },
      set(v) {
        this.fuelPriceForecastSourceObject = v;
        const valueCopy = this.value;
        valueCopy.input.fuel_price_inputs.fuel_price_forecast.fuel_forecast_id = v.series_id;
        this.$emit("input", valueCopy);
      },
    },
    fuelPriceHistoricalSourceElement: {
      get() {
        return this.fuelPriceHistoricalSourceObject;
      },
      set(v) {
        this.fuelPriceHistoricalSourceObject = v;
        const valueCopy = this.value;
        valueCopy.input.fuel_price_inputs.historical_fuel_price.fuel_historical_id = v["Series ID"];
        this.$emit("input", valueCopy);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.migrateLegacyAncillaryLabels();
        if (this.migrateAncillaryMarket()) {
          this.$emit("input", this.value);
        }
      },
    },
    energyMarketSelection(val) {
      // Once energy no longer needs a market/node, clear the stale selection
      // instead of leaving it behind.
      if (!val) {
        this.$emit("input", this.marketFunction());
      }
    },
    markets: {
      handler(val) {
        this.$emit("input", this.marketFunction(val));
      },
      deep: true,
    },
    ancillaryMarkets: {
      handler(val) {
        this.debugAncillaryWatcherFireCount += 1;
        this.$emit("input", this.ancillaryMarketFunction(val));
      },
      deep: true,
    },
    ancillaryActive(val) {
      // Once ancillary pricing is turned off, clear its stale market/node
      // selection instead of leaving it behind.
      if (!val) {
        this.$emit("input", this.ancillaryMarketFunction());
      }
    },
    forecastStartYear(val) {
      if (!this.value.input.settings.end_of_forecast_date && val >= 1900) {
        const valueCopy = this.value;
        valueCopy.input.settings.end_of_forecast_date = val;
        this.$emit("input", valueCopy);
      }
    },
    toggleHFV(val) {
      const valueCopy = this.value;
      if (val && valueCopy.input.settings.hfv === -1) {
        valueCopy.input.settings.hfv = 1;
      } else if (!val) {
        valueCopy.input.settings.hfv = -1;
      }
      this.$emit("input", valueCopy);
    },
    toggleLFV(val) {
      const valueCopy = this.value;
      if (val && valueCopy.input.settings.lfv === -1) {
        valueCopy.input.settings.lfv = 1;
      } else if (!val) {
        valueCopy.input.settings.lfv = -1;
      }
      this.$emit("input", valueCopy);
    },
    fuelPriceForecastSource(v) {
      if (["market", "default"].includes(v)) {
        delete this.value.input.fuel_price_inputs.fuel_price_forecast.values;
      }
    },
    fuelPriceHistoricalSource(v) {
      if (["market", "default"].includes(v)) {
        delete this.value.input.fuel_price_inputs.historical_fuel_price.profile;
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (this.value.input.settings.hfv !== -1) {
      this.toggleHFV = true;
    }
    if (this.value.input.settings.lfv !== -1) {
      this.toggleLFV = true;
    }
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    migrateLegacyAncillaryLabels() {
      const { market_name: marketName, ancillary_settings: ancillarySettings } =
        this.value.input.energy_ancillary_inputs;
      const effectiveMarketName = ancillarySettings.market_name || marketName;
      if (migrateLegacyAncillaryLabels(effectiveMarketName, ancillarySettings)) {
        this.$emit("input", this.value);
      }
    },
    migrateAncillaryMarket() {
      // Forecasts saved before ancillary had its own market fields relied on
      // the shared top-level market/node/index/zone. Copy that into
      // ancillary_settings once, up front, so it survives even if the user
      // only edits Energy's price source and never touches Ancillary's own
      // market picker (which would otherwise leave ancillary without a
      // market once energy's shared fields get cleared).
      const {
        market_name: marketName,
        index,
        node_id: nodeId,
        zone_id: zoneId,
        ancillary_settings: ancillarySettings,
      } = this.value.input.energy_ancillary_inputs;
      if (
        ancillarySettings.active &&
        ancillarySettings.ancillary_price_source === "market" &&
        !ancillarySettings.market_name &&
        marketName
      ) {
        this.$set(ancillarySettings, "market_name", marketName);
        this.$set(ancillarySettings, "index", index);
        this.$set(ancillarySettings, "node_id", nodeId);
        this.$set(ancillarySettings, "zone_id", zoneId);
        return true;
      }
      return false;
    },
    marketFunction(val = null) {
      const valueCopy = this.value;
      if (val) {
        valueCopy.input.energy_ancillary_inputs.market_name = val.name;
        valueCopy.input.energy_ancillary_inputs.index = val.index;
        valueCopy.input.energy_ancillary_inputs.node_id = val.node;
        valueCopy.input.energy_ancillary_inputs.zone_id = val.zone || null;
      } else {
        delete valueCopy.input.energy_ancillary_inputs.market_name;
        delete valueCopy.input.energy_ancillary_inputs.index;
        delete valueCopy.input.energy_ancillary_inputs.node_id;
        delete valueCopy.input.energy_ancillary_inputs.zone_id;
      }
      return valueCopy;
    },
    ancillaryMarketFunction(val = null) {
      const valueCopy = this.value;
      const ancillarySettings = valueCopy.input.energy_ancillary_inputs.ancillary_settings;
      const previousMarketName = ancillarySettings.market_name;
      const nextMarketName = val ? val.name : null;
      if (val) {
        this.$set(ancillarySettings, "market_name", val.name);
        this.$set(ancillarySettings, "index", val.index);
        this.$set(ancillarySettings, "node_id", val.node);
        this.$set(ancillarySettings, "zone_id", val.zone || null);
      } else {
        delete ancillarySettings.market_name;
        delete ancillarySettings.index;
        delete ancillarySettings.node_id;
        delete ancillarySettings.zone_id;
      }

      // Reset Ancillary type selection and per-type custom profiles on market change
      if (nextMarketName !== previousMarketName) {
        const canonicalDefault = this.legacyAncillarySelection[nextMarketName];
        ancillarySettings.ancillary_price_labels = canonicalDefault
          ? [...canonicalDefault]
          : [...this.legacyAncillaryLabels];
        this.$set(ancillarySettings, "custom_profiles", {});
      }
      return valueCopy;
    },
    ancillaryCustomProfile(label) {
      const { custom_profiles: customProfiles } =
        this.value.input.energy_ancillary_inputs.ancillary_settings;
      return customProfiles ? customProfiles[label] : undefined;
    },
    setAncillaryCustomProfile(label, val) {
      const valueCopy = this.value;
      const ancillarySettings = valueCopy.input.energy_ancillary_inputs.ancillary_settings;
      if (!ancillarySettings.custom_profiles) {
        this.$set(ancillarySettings, "custom_profiles", {});
      }
      this.$set(ancillarySettings.custom_profiles, label, val);
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
    ancillaryProductLabelText(label) {
      const option = (this.ancillaryProductOptions || []).find((item) => item.value === label);
      return option ? option.text : label;
    },
    change() {
      const valueCopy = this.value;
      this.$emit("input", valueCopy);
      this.$emit("change");
    },
  },
};
</script>

<style></style>
